<!-- Laid out as the chat window is: a coloured header bar, a side pane, the messages on a washed
     canvas, and a composer bar at the foot. The frame around it all is _cura-window.scss. -->
<div class="cura-window cura-conversation"
     [appResizableOverlayPane]="floatingDialogLayoutKey"
     [resizeMinWidth]="320"
     [resizeMinHeight]="240"
     [class.drag-over]="isDraggingOver"
     (dragover)="onDragOver($event)" (dragleave)="onDragLeave($event)" (drop)="onDrop($event)">

    <!-- Drop anywhere on the dialog. The overlay is translucent so the conversation stays
         visible underneath, and pointer-events are off so it cannot swallow its own drop. -->
    <div class="dialog-drop-overlay" *ngIf="isDraggingOver">
        <div class="drop-card">
            <mat-icon>upload_file</mat-icon>
            <div class="drop-title">Drop to attach</div>
            <div class="drop-sub">PDF, image or Word document</div>
        </div>
    </div>

    <!-- The template menu, opened from the side pane or, in a narrow window, from the strip above
         the conversation. Declared here so both triggers can reach it. -->
    <mat-menu #templateMenu="matMenu" class="cura-menu">
        <div class="cura-menu-label">Template</div>
        <button mat-menu-item [class.selected]="!selectedNoteTypeId" (click)="onNoteTypeSelected(null)">
            <mat-icon>{{ !selectedNoteTypeId ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
            <span>None</span>
        </button>
        <button mat-menu-item *ngFor="let noteType of forCuraNoteTypes"
            [class.selected]="selectedNoteTypeId === noteType.id" (click)="onNoteTypeSelected(noteType.id)">
            <mat-icon>{{ selectedNoteTypeId === noteType.id ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
            <span>{{ noteType.label }}</span>
        </button>
    </mat-menu>

    <div class="cura-header" appDraggableOverlayPane [dragLayoutKey]="floatingDialogLayoutKey">
        <mat-icon class="kind">auto_awesome</mat-icon>
        <div class="title">
            <span class="name">Cura</span>
            <span class="subtitle" *ngIf="patient?.name; else plainSubtitle">
                Medical AI assistant · {{ patient.name }} {{ patient.surname }}
            </span>
            <ng-template #plainSubtitle><span class="subtitle">Your medical AI assistant</span></ng-template>
        </div>
        <!-- Forms filled for this patient before: a review that closed with the chat can be reopened. -->
        <button mat-icon-button [matMenuTriggerFor]="previousFormsMenu" *ngIf="previousFills.length"
            matTooltip="Forms filled for this patient" aria-label="Forms filled for this patient"
            (click)="$event.stopPropagation()">
            <mat-icon>fact_check</mat-icon>
        </button>
        <mat-menu #previousFormsMenu="matMenu" xPosition="before" class="cura-menu">
            <div class="cura-menu-label">Forms filled for this patient</div>
            <button mat-menu-item *ngFor="let f of previousFills" (click)="reopenFill(f)">
                <mat-icon>{{ f.status === 'accepted' ? 'task_alt' : 'rate_review' }}</mat-icon>
                <span>{{ f.fileName || 'Form' }}</span>
                <small>{{ f.createdAt | date:'d MMM, HH:mm' }} · {{ f.status === 'accepted' ? 'accepted' : 'awaiting review' }}</small>
            </button>
        </mat-menu>
        <button mat-icon-button mat-dialog-close matTooltip="Close" aria-label="Close">
            <mat-icon>close</mat-icon>
        </button>
    </div>

    <div class="cura-body">
        <!-- Outside the blocks hidden while locked: inside them this message could never show. -->
        <div class="ai-locked" *ngIf="lockProcessing && !isPremium" data-test="ai-locked">
            <mat-icon>error</mat-icon>
            <p class="p-l-10">You do not have enough AI tokens to continue utilizing AI features. Please recharge
                your token balance by navigating to settings and selecting the "Subscriptions &amp; Usage" tab.</p>
        </div>

        <!-- The quick actions and the template pick, as the chat window's left pane. -->
        <aside class="cura-side" *ngIf="!lockProcessing">
            <div class="side-head">Quick actions</div>
            <div class="side-actions">
                <ng-container *ngFor="let action of defaultActions">
                    <button mat-stroked-button class="quick-action" (click)="sendMessage(action)"
                        [disabled]="selectedNoteTypeId !== null">
                        {{action.text}}
                    </button>
                </ng-container>
            </div>

            <!-- Note Type Template Selection -->
            <!-- The template to write to: a field that opens a menu of the choices, as the chat's ⋮ menu does. -->
            <div class="note-type-selection-container" *ngIf="forCuraNoteTypes.length > 0">
                <div class="side-head">Template</div>
                <button type="button" class="cura-select full" [matMenuTriggerFor]="templateMenu"
                    matTooltip="Write the reply to a note template (optional)">
                    <span class="cura-select-text" [class.placeholder]="!selectedNoteTypeId">{{ selectedNoteTypeLabel || 'No template' }}</span>
                    <mat-icon class="cura-select-clear" *ngIf="selectedNoteTypeId" matTooltip="Clear selection"
                        (click)="$event.stopPropagation(); clearNoteTypeSelection()">close</mat-icon>
                    <mat-icon class="cura-select-arrow">arrow_drop_down</mat-icon>
                </button>
            </div>
        </aside>

        <div class="cura-main" *ngIf="!lockProcessing">
            <!-- A narrow window: the side pane gives way to this strip, so the conversation keeps the room. -->
            <div class="cura-strip">
                <button type="button" class="cura-select" [matMenuTriggerFor]="quickMenu">
                    <mat-icon class="cura-select-icon">bolt</mat-icon>
                    <span class="cura-select-text">Quick actions</span>
                    <mat-icon class="cura-select-arrow">arrow_drop_down</mat-icon>
                </button>
                <mat-menu #quickMenu="matMenu" class="cura-menu">
                    <div class="cura-menu-label">Quick actions</div>
                    <button mat-menu-item *ngFor="let action of defaultActions" (click)="sendMessage(action)"
                        [disabled]="selectedNoteTypeId !== null">
                        <span>{{ action.text }}</span>
                    </button>
                </mat-menu>
                <button type="button" class="cura-select" *ngIf="forCuraNoteTypes.length > 0"
                    [matMenuTriggerFor]="templateMenu" matTooltip="Write the reply to a note template (optional)">
                    <span class="cura-select-text" [class.placeholder]="!selectedNoteTypeId">{{ selectedNoteTypeLabel || 'No template' }}</span>
                    <mat-icon class="cura-select-clear" *ngIf="selectedNoteTypeId" matTooltip="Clear selection"
                        (click)="$event.stopPropagation(); clearNoteTypeSelection()">close</mat-icon>
                    <mat-icon class="cura-select-arrow">arrow_drop_down</mat-icon>
                </button>
            </div>
            <div class="cura-messages" #messagesContainer>
                <div class="token-balance-warning" *ngIf="lowTokenBalance !== null">
                    <mat-icon>warning</mat-icon>
                    <p class="p-l-10">Your token balance is currently R{{ lowTokenBalance | number: '1.2-2' }}. Please
                        recharge soon to ensure you are able to continue utilizing AI features.</p>
                </div>

                <!-- Cura opens the conversation, as the other side of a chat would. -->
                <div class="cura-msg theirs" *ngIf="!messages?.length">
                    <div class="bubble tail">
                        <span class="sender">Cura</span>
                        <p>Hi, I'm <strong>Cura</strong>, your Medical AI Assistant. You can ask me questions about
                            this patient's file.</p>
                        <p>I can generate a summary, write a report, or tell you more about a specific event within the
                            timeline. I am not yet able to perform actions on the patient file such as creating notes or
                            scheduling appointments.</p>
                        <p>Use the quick actions or type your own message. Attach a form and ask me to fill it in.</p>
                    </div>
                </div>

                <ng-container *ngIf="messages?.length">
                    <div *ngFor="let message of messages" class="cura-msg" [class.mine]="message.isSent"
                        [class.theirs]="!message.isSent">
                        <!-- A form fill: its own bubble, in the flow of the conversation -->
                        <div class="bubble tail form-fill-bubble" *ngIf="message.formFill as ff" [class.failed]="ff.failed">
                            <div class="ff-head">
                                <mat-icon>{{ ff.failed ? 'error_outline' : (ff.done ? 'fact_check' : 'hourglass_top') }}</mat-icon>
                                <span class="ff-name">{{ ff.fileName }}</span>
                            </div>

                            <ng-container *ngIf="!ff.done && !ff.failed">
                                <mat-progress-bar mode="determinate" [value]="ff.progress"></mat-progress-bar>
                                <div class="ff-status">{{ ff.message }}</div>
                            </ng-container>

                            <div class="ff-counts" *ngIf="ff.counts">
                                <span class="ff-chip filled">{{ ff.counts.filled }} filled</span>
                                <span class="ff-chip blank" *ngIf="ff.counts.blank">{{ ff.counts.blank }} blank</span>
                                <span class="ff-chip check" *ngIf="ff.counts.needsCheck">{{ ff.counts.needsCheck }} to check</span>
                                <span class="ff-chip total">of {{ ff.counts.total }}</span>
                            </div>

                            <div class="message-text ff-prose" [innerHTML]="message.message | formatMessage"
                                *ngIf="message.message"></div>

                            <div class="ff-actions" *ngIf="ff.done">
                                <button mat-flat-button class="tone-button" (click)="openFormReview(ff)">
                                    <mat-icon>rate_review</mat-icon> Review the form
                                </button>
                                <!-- Both are refused until the form is accepted, so they appear then. -->
                                <ng-container *ngIf="ff.accepted">
                                    <button mat-stroked-button class="quiet-button" (click)="downloadFill(ff)" [disabled]="ff.busy">
                                        <mat-icon>file_download</mat-icon> Download
                                    </button>
                                    <button mat-stroked-button class="quiet-button" (click)="addFillToTimeline(ff)"
                                        [disabled]="ff.busy || ff.promoted">
                                        <mat-icon>{{ ff.promoted ? 'check' : 'post_add' }}</mat-icon>
                                        {{ ff.promoted ? 'In the timeline' : 'Add to timeline' }}
                                    </button>
                                </ng-container>
                            </div>
                            <div class="ff-hint" *ngIf="ff.done && !ff.accepted">
                                Review and accept it before downloading — nothing leaves here unchecked.
                            </div>
                        </div>

                        <div class="bubble tail" *ngIf="!message.formFill">
                            <span *ngIf="!message.isSent" class="sender">{{ message.sender?.name }}
                                {{message.sender?.surname}}</span>
                            <span class="message-text" [innerHTML]="message.message | formatMessage"
                                *ngIf="!message.isEditing"></span>
                            <mat-form-field appearance="outline" class="edit-field" *ngIf="message.isEditing">
                                <mat-label>Message</mat-label>
                                <textarea matInput appFindReplace [(ngModel)]="message.message" rows="20"></textarea>
                            </mat-form-field>
                            <div class="turn-status" *ngIf="message.isLoading">
                                <mat-spinner mode="indeterminate" diameter="16"></mat-spinner>
                                <span>{{ message.status || 'Working on it…' }}</span>
                            </div>
                        </div>

                        <!-- The generic message actions (edit / copy / add to timeline / download /
                             email / WhatsApp) apply to ASSISTANT PROSE. A form-fill bubble carries its
                             own actions and none of these can act on it, so they are suppressed —
                             showing a download button that cannot download is worse than none. -->
                        <div class="bubble-tools" *ngIf="!message.isLoading && !message.isSent && !message.formFill">
                            <ng-container *ngIf="!message.isEditing">
                                <mat-icon class="pointer" (click)="toggleEditing(message)"
                                    matTooltip="Edit the content of this message">edit</mat-icon>
                                <mat-icon class="pointer" (click)="copyMessage(message)" matTooltip="Copy this message">
                                    {{message.isCopied ? 'check' : 'content_copy'}}</mat-icon>
                                <mat-icon class="pointer" (click)="addMessageToTimeline(message)"
                                    matTooltip="Add this message to the patient timeline">post_add</mat-icon>

                                <ng-container *ngIf="canSign; else cantSign">
                                    <mat-icon matTooltip="Download this message as PDF" [matMenuTriggerFor]="downloadMenu"
                                        class="pointer" (click)="$event.stopPropagation()">file_download</mat-icon>

                                    <mat-menu #downloadMenu="matMenu" class="cura-menu cura-menu-form">
                                        <div class="cura-menu-label">Download as PDF</div>
                                        <mat-checkbox [(ngModel)]="includeSignatureOnPdf" class="checkbox"
                                            (click)="$event.stopPropagation()">
                                            {{isPhysician ?'Attach Signature' : 'Attach Doctor\'s Signature'}}
                                        </mat-checkbox>
                                        <mat-form-field *ngIf="hasSignaturePermission" appearance="outline"
                                            class="full-width" (keydown)="handleMenuKeydown($event)"
                                            (click)="$event.stopPropagation()">
                                            <mat-label>Select Doctor's Signature</mat-label>
                                            <mat-select [(ngModel)]="selectedDoctor" panelClass="cura-select-panel">
                                                <mat-option *ngFor="let doctor of groupUsers" [value]="doctor">
                                                    {{doctor.title}} {{ doctor.name }} {{doctor.surname}}
                                                </mat-option>
                                            </mat-select>
                                        </mat-form-field>

                                        <mat-form-field appearance="outline" class="full-width"
                                            (keydown)="handleMenuKeydown($event)" (click)="$event.stopPropagation()">
                                            <mat-label>Document Date</mat-label>
                                            <input matInput [matDatepicker]="pdfDatePicker" [(ngModel)]="pdfDate" placeholder="DD/MM/YYYY">
                                            <mat-datepicker-toggle matSuffix [for]="pdfDatePicker"></mat-datepicker-toggle>
                                            <mat-datepicker #pdfDatePicker></mat-datepicker>
                                        </mat-form-field>

                                        <button mat-raised-button color="primary" class="full-width"
                                            (click)="downloadAsPdf($event, message)">
                                            Download PDF <mat-icon>file_download</mat-icon>
                                        </button>
                                    </mat-menu>
                                </ng-container>
                                <ng-template #cantSign>
                                    <mat-icon matTooltip="Download this message as PDF"
                                        (click)="downloadAsPdf($event, message)" class="pointer">file_download</mat-icon>
                                </ng-template>

                                <mat-icon class="pointer" matTooltip="Send this via email" [matMenuTriggerFor]="emailMenu" *ngIf="!stateUnit"
                                    (click)="$event.stopPropagation()">mail</mat-icon>

                                <mat-icon class="pointer" matTooltip="Send this via WhatsApp"
                                    appWhatsappDialogTrigger
                                    [patientId]="data.patientId"
                                    [patient]="patient"
                                    [initialMessage]="getMessagePlainText(message)"
                                    svgIcon="whatsapp"></mat-icon>

                                <mat-menu #emailMenu="matMenu" class="cura-menu cura-menu-form email-menu-panel">
                                    <div mat-menu-content (click)="$event.stopPropagation()">
                                        <div class="cura-menu-label">Send by email</div>

                                        <mat-checkbox [(ngModel)]="sendingDetails.patchPatient" class="checkbox"
                                            (change)="patchPatientEmailDetails()">Send To Patient</mat-checkbox>
                                        <mat-checkbox *ngIf="canSign" [(ngModel)]="includeSignatureOnPdf" class="checkbox"
                                            (click)="$event.stopPropagation()">
                                            {{isPhysician ?'Attach Signature' : 'Attach Doctor\'s Signature'}}
                                        </mat-checkbox>

                                        <mat-form-field *ngIf="hasSignaturePermission" appearance="outline"
                                            class="full-width" (keydown)="handleMenuKeydown($event)"
                                            (click)="$event.stopPropagation()">
                                            <mat-label>Select Doctor's Signature</mat-label>
                                            <mat-select [(ngModel)]="selectedDoctor" panelClass="cura-select-panel">
                                                <mat-option *ngFor="let doctor of groupUsers" [value]="doctor">
                                                    {{doctor.title}} {{ doctor.name }} {{doctor.surname}}
                                                </mat-option>
                                            </mat-select>
                                        </mat-form-field>
                                        <app-recipient-search [patient]="patient" label="Quick Select Recipient"
                                            placeholder="Search for recipient..."
                                            (recipientSelected)="onRecipientSelected($event)">
                                        </app-recipient-search>
                                        <mat-form-field appearance="outline" class="full-width"
                                            (keydown)="handleMenuKeydown($event)">
                                            <mat-label>Recipient Name</mat-label>
                                            <input matInput [(ngModel)]="sendingDetails.name">
                                        </mat-form-field>
                                        <mat-form-field appearance="outline" class="full-width">
                                            <mat-label>Recipient Email</mat-label>
                                            <input matInput [(ngModel)]="sendingDetails.email">
                                        </mat-form-field>
                                        <mat-form-field appearance="outline" class="full-width"
                                            (keydown)="handleMenuKeydown($event)" (click)="$event.stopPropagation()">
                                            <mat-label>Document Date</mat-label>
                                            <input matInput [matDatepicker]="pdfDatePickerEmail" [(ngModel)]="pdfDate" placeholder="DD/MM/YYYY">
                                            <mat-datepicker-toggle matSuffix [for]="pdfDatePickerEmail"></mat-datepicker-toggle>
                                            <mat-datepicker #pdfDatePickerEmail></mat-datepicker>
                                        </mat-form-field>
                                        <button mat-raised-button color="primary" class="full-width"
                                            [disabled]="!sendingDetails.email" (click)="emailMessage(message)"
                                            matTooltip="Send to this email address">
                                            <mat-icon>send</mat-icon> Send Email
                                        </button>
                                    </div>
                                </mat-menu>
                            </ng-container>
                            <ng-container *ngIf="message.isEditing">
                                <mat-icon class="pointer" (click)="toggleEditing(message)"
                                    matTooltip="Save the changes to this message">save</mat-icon>
                            </ng-container>
                        </div>
                    </div>
                </ng-container>
                <p class="retention-note" *ngIf="messages?.length">
                    These messages are not kept: copy anything important before closing. Cura can make
                    mistakes, so check important information.
                </p>
            </div>

            <div class="cura-composer">
                <!-- Attachment chips. Sit above the input so what CURA can see is visible while typing. -->
                <div class="attachment-chips" *ngIf="attachments.length || uploadingAttachments || attachmentError">
                    <div class="attachment-chip" *ngFor="let attachment of attachments"
                        [matTooltip]="attachment.fileName">
                        <mat-icon class="chip-icon">{{ attachmentIcon(attachment) }}</mat-icon>
                        <span class="chip-name">{{ attachment.fileName }}</span>
                        <span class="chip-pages" *ngIf="attachment.pageCount">{{ attachment.pageCount }}p</span>
                        <mat-icon class="chip-remove pointer" (click)="removeAttachment(attachment)">close</mat-icon>
                    </div>

                    <div class="attachment-chip uploading" *ngIf="uploadingAttachments">
                        <mat-spinner diameter="14"></mat-spinner>
                        <span class="chip-name">Uploading…</span>
                    </div>

                    <div class="attachment-error" *ngIf="attachmentError">
                        <mat-icon class="chip-icon">error_outline</mat-icon>
                        <span>{{ attachmentError }}</span>
                    </div>
                </div>

                <!-- One rounded field holding attach, the text and Send, as the chat's composer. -->
                <div class="compose-row">
                    <button mat-icon-button class="attach" type="button" (click)="openAttachmentPicker()"
                        [disabled]="loading || uploadingAttachments || attachments.length >= maxAttachments"
                        matTooltip="Attach a file (PDF, image or .docx)" aria-label="Attach a file">
                        <mat-icon>attach_file</mat-icon>
                    </button>
                    <input class="compose-text" [(ngModel)]="newMessage" [placeholder]="messagePlaceholder"
                        (keyup.enter)="sendMessage()" (paste)="onPaste($event)" [disabled]="loading"
                        aria-label="Message to Cura" />
                    <button class="send" type="button" (click)="sendMessage()" [disabled]="loading"
                        matTooltip="Send" aria-label="Send">
                        <mat-icon>send</mat-icon>
                    </button>
                </div>

                <input #attachmentInput type="file" multiple hidden [accept]="attachmentAccept"
                    (change)="onAttachmentPicked($event)" />
            </div>
        </div>
    </div>
</div>

<ng-template #addNoteDialog>
    <h1 mat-dialog-title>Note Details</h1>
    <div mat-dialog-content>
        <div class="p-t-20 field-column">

            <mat-form-field appearance="outline">
                <mat-label>Date</mat-label>
                <input matInput [matDatepicker]="dateDatePicker" [(ngModel)]="addingNote.date" placeholder="DD/MM/YYYY"
                    [max]="todayDate">
                <mat-datepicker-toggle matSuffix [for]="dateDatePicker"></mat-datepicker-toggle>
                <mat-datepicker #dateDatePicker></mat-datepicker>
            </mat-form-field>


            <mat-form-field appearance="outline">
                <mat-label>{{addingNote.isProcedure ? 'Procedure Title' : 'Note Title'}}</mat-label>
                <input matInput [(ngModel)]="addingNote.title">
            </mat-form-field>


            <mat-form-field appearance="outline">
                <mat-label>Notes</mat-label>
                <textarea matInput cdkTextareaAutosize="true" rows="10" [(ngModel)]="addingNote.notes"></textarea>
            </mat-form-field>

            <div class="toggle-container" *ngIf="!stateUnit">
                <mat-slide-toggle [(ngModel)]="addingNote.paidCash" (change)="toggleCodeStatus(addingNote, 'up-front')"
                    color="primary">Paid Up
                    Front</mat-slide-toggle>
                <mat-slide-toggle [(ngModel)]="addingNote.noCharge" color="primary"
                    (change)="toggleCodeStatus(addingNote, 'no-charge')" class="m-l-5">No
                    Charge</mat-slide-toggle>
            </div>

            <app-procedure-code-selector [initialCodes]="addingNote.procedureCodes"
                (codesChanged)="onSelectedProcedureCodesChanged($event, addingNote)"
                [patient]="patient"></app-procedure-code-selector>

            <app-file-upload [fileObjects]="[]" [allowMultiple]="true" [allowedFileTypes]="'image/*, application/pdf'"
                title="Note Media" (fileUpload)="onNoteMediaUpload($event)" (fileDelete)="onNoteMediaDelete($event)"
                (filesEdited)="onFilesEdited($event)" [cameraEnabled]="true" [enableEditing]="true">
            </app-file-upload>

            <p class="hint"><strong>Note: </strong>MedicAI can make mistakes. We advise that you check the content
                of the output carefully before accepting it into the patient file.</p>

        </div>

    </div>
    <div mat-dialog-actions class="dialog-actions">
        <button mat-button [mat-dialog-close]="null">Cancel</button>
        <button mat-raised-button color="primary" [disabled]="!addingNote.date || !addingNote.title"
            [mat-dialog-close]="addingNote">Save</button>
    </div>

</ng-template>